import { _ } from "../../../polyglot/polyglot.js";
import { formatAsMonthAndYear } from "../../../helpers/date-formatting-util.js";
import { makeMonitorRepository } from "../../../repositories/repositoryBuilders.js";
import AbstractImporter from "../AbstractImporter.js";
import DropdownSelectionStep from "../DropdownSelectionStep.js";
import session from "../../../session.js";
import TfsoIntegration from "../../../../models/integration/TfsoIntegration.js";
import UsernamePasswordLoginStep from "./UsernamePasswordLoginStep.js";

/**
 * `TfsoImporter` Import wizard for 24SevenOffice.
 */
export default class TfsoImporter extends AbstractImporter {
	constructor() {
		super(...arguments);

		const antiForgeryToken = session.getAntiForgeryToken();

		this._tfsoLoginRepository = makeMonitorRepository({
			route: "/Shared/Import/TfsoLogin/",
			antiForgeryToken,
		});

		this._tfsoRepository = makeMonitorRepository({
			route: "/Shared/Import/Tfso/#tfsoCompanyId",
			antiForgeryToken,
		});
	}

	_wizardSteps() {
		return [
			this._createLoginStep(),
			this._createClientSelectionStep(),
			this._createFiscalYearSelectionStep(),
		];
	}

	// Step 1: Get the username and password from the user to authenticate
	// with tfso.
	_createLoginStep() {
		return new UsernamePasswordLoginStep({
			submitCallback: this._loginToTfso.bind(this),
		});
	}

	// Step 1 ⇒ Step 2: Authenticate with Tfso and expect a list of clients in response.
	_loginToTfso({ onDone, step }) {
		const username = this._context.credentials.username;
		const password = this._context.credentials.password;
		const currentCompanyId = this._context.company.isNullCompany()
			? null
			: this._context.company.getLegacyId();

		this._tfsoLoginRepository
			.save({
				filter: {
					companyId: currentCompanyId,
				},
				model: { username, password },
				notify: false,
			})
			.then((tfsoCompanyData) => {
				this._context.tfsoCompanies = tfsoCompanyData.clients;

				this._context.selectedTfsoCompany =
					tfsoCompanyData.selectedClient;

				onDone();
			})
			.catch((error) => {
				step.addSubmitError(this._errorToMessage(error));
				onDone();
			});
	}

	// Step 2: Ask the user to select which client to import data for.
	_createClientSelectionStep() {
		return new DropdownSelectionStep({
			dropdownLabel: _("Available clients"),
			getItemsFromContext: (context) =>
				this._createCompanyList(
					context.tfsoCompanies,
					context.selectedTfsoCompany,
				),
			submitCallback: this._tfsoCompanyToImportData.bind(this),
			name: "tfso-companies",
		});
	}

	_createCompanyList(companyList, selectedCompany) {
		if (selectedCompany)
			return [
				{
					label: selectedCompany.name,
					value: selectedCompany,
				},
			];

		return companyList.map((tfsoCompany) => ({
			label: tfsoCompany.name,
			value: tfsoCompany,
		}));
	}

	// Step 2 ⇒ Step 3: get all fiscalyears for the selected client.
	_tfsoCompanyToImportData({ onDone, step, selection }) {
		this._context.selectedTfsoCompany = selection;

		this._tfsoRepository
			.get({
				filter: {
					tfsoCompanyId: this._context.selectedTfsoCompany.id,
				},
			})
			.then((fiscalYears) => {
				this._context.fiscalYears = fiscalYears;
				onDone();
			})
			.catch((error) => {
				step.addSubmitError(this._errorToMessage(error));
				onDone();
			});
	}

	// Step 3: Ask the user to select which fiscalYear to import.
	_createFiscalYearSelectionStep() {
		return new DropdownSelectionStep({
			dropdownLabel: _("Available fiscal years"),
			getItemsFromContext: (context) =>
				context.fiscalYears.map((year) => ({
					label: `${formatAsMonthAndYear(
						year.start,
					)} - ${formatAsMonthAndYear(year.end)}`,
					value: year,
				})),
			submitCallback: this._fiscalYearToImportData.bind(this),
			name: "tfso-fiscalyears",
		});
	}

	// Step 3 ⇒ Step 4: Post the fiscalYear and trigger the import
	_fiscalYearToImportData({ onDone, step, selection }) {
		this._tfsoRepository
			.save({
				filter: {
					tfsoCompanyId: this._context.selectedTfsoCompany.id,
				},
				model: selection,
				notify: false,
			})
			.then((data) => {
				this._context.importData = data;
				onDone();
			})
			.catch((error) => {
				step.addSubmitError(this._errorToMessage(error));
				onDone();
			});
	}

	_errorToMessage(error) {
		switch (error.responseJSON.typeOfError) {
			case "RequestTimeout": {
				return _(
					"The 24SevenOffice server is not currently responding, please try again later.",
				);
			}
			case "NotAuthenticated": {
				return _(
					"It seems like that your login session at 24SevenOffice has timed out, please try again.",
				);
			}
			case "NoTransactionFound": {
				return _(
					"Can not find any transactions for the selected client.",
				);
			}
			case "TfsoServerIssue":
			case "UnknownApiException": {
				return _(
					"At the moment there seems to be problems with the {externalAPI} server. Please try again in a few minutes.",
					{ externalAPI: "24SevenOffice API" },
				);
			}
			default: {
				return error.responseJSON.message;
			}
		}
	}
}

TfsoImporter.integrationType = TfsoIntegration.getType();
